import axios from "axios";

/**
 * Resolve the API base URL.
 *
 * Priority:
 *   1. If REACT_APP_BACKEND_URL is set AND points at the same origin we're
 *      currently on, use it. Same-origin requests bypass CORS entirely.
 *   2. If REACT_APP_BACKEND_URL points at a DIFFERENT origin (typical post-
 *      deployment URL drift, where the bundle was built against a preview URL
 *      but is now served from `*.emergent.host`), fall back to a same-origin
 *      relative URL ("/api"). Avoids "Network Error" from CORS rejection of
 *      `Access-Control-Allow-Origin: *` + `withCredentials: true`.
 *   3. In Node/SSR contexts (no window), use whatever env var is set.
 */
function resolveApiBase() {
  const envUrl = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  if (typeof window === "undefined") return envUrl ? `${envUrl}/api` : "/api";
  if (!envUrl) return "/api";
  try {
    const envOrigin = new URL(envUrl).origin;
    if (envOrigin === window.location.origin) return `${envUrl}/api`;
    // Cross-origin URL — prefer same-origin to avoid CORS surprises.
    return "/api";
  } catch {
    return "/api";
  }
}

export const API = resolveApiBase();

export const http = axios.create({
  baseURL: API,
  withCredentials: true,
});

export function formatApiErrorDetail(detail) {
  if (detail == null) return "";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

/**
 * Convert an axios error into a user-friendly message.
 * Handles the common "Network Error" / no-response case so the user sees
 * something actionable instead of axios's terse default.
 */
export function describeApiError(err, fallback = "Something went wrong. Please try again.") {
  if (!err) return fallback;
  // Server responded with an error payload
  const detail = formatApiErrorDetail(err.response?.data?.detail);
  if (detail) return detail;
  // No response at all = network/CORS/offline
  if (!err.response) {
    if (err.code === "ERR_NETWORK" || /network/i.test(err.message || "")) {
      return "Couldn't reach the server. Check your connection and try again.";
    }
    if (err.code === "ECONNABORTED" || /timeout/i.test(err.message || "")) {
      return "The server took too long to respond. Please try again.";
    }
  }
  return err.message || fallback;
}
